import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Languages, MessageSquare, RefreshCw } from 'lucide-react';

const LANGUAGE_NAMES = { en: 'English', es: 'Spanish', vi: 'Vietnamese', ko: 'Korean' };
const SMS_LABELS = { sent: 'Text sent', failed: 'Text failed', no_consent: 'No text consent', not_configured: 'Texts off',
  opted_out: 'Opted out', invalid_number: 'Bad number', pending: 'Text pending', sending: 'Text sending' };

const when = iso => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' }) : '—';

const CallRow = ({ call, highlighted }) => {
  const intake = call.intake || {};
  return <li className={`admin-call ${call.urgent ? 'is-urgent' : ''} ${highlighted ? 'is-highlighted' : ''}`} data-testid={`admin-call-${call.call_id}`} id={`call-${call.call_id}`}>
    <div className="admin-call-head">
      <span className="admin-call-when">{when(call.started_at)} PT · {call.status}</span>
      {call.urgent && <span className="admin-urgent-tag" data-testid={`admin-call-urgent-${call.call_id}`}><AlertTriangle size={11} /> Urgent</span>}
    </div>
    <strong data-testid={`admin-call-name-${call.call_id}`}>{intake.name || 'Visitor'}{intake.phone ? ` · ${intake.phone}` : ''}</strong>
    {intake.legal_issue && <p>{intake.legal_issue}</p>}
    {call.urgent && <p className="admin-call-reason" data-testid={`admin-call-reason-${call.call_id}`}>{call.urgent_reason} · staff alert: {call.alert_status || 'not sent'}</p>}
    {call.summary_status === 'ready' && call.summary && <p className="admin-call-summary">{call.summary}</p>}
    <div className="admin-call-meta">
      <span data-testid={`admin-call-language-${call.call_id}`}><Languages size={11} /> {LANGUAGE_NAMES[call.language] || 'English'}</span>
      <span data-testid={`admin-call-sms-${call.call_id}`}><MessageSquare size={11} /> {call.sms_opted_out ? 'Opted out' : SMS_LABELS[call.sms_status] || 'Text pending'}</span>
      <span>Email: {call.email_status}</span>
    </div>
  </li>;
};

export const AdminCalls = ({ token, request }) => {
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [data, setData] = useState({ calls: [], urgent_count: 0 });
  const [loading, setLoading] = useState(false);
  const highlighted = new URLSearchParams(window.location.search).get('call');

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await request(`/admin/calls?urgent=${urgentOnly}`, token)); } catch { /* keep the previous list */ } finally { setLoading(false); }
  }, [request, token, urgentOnly]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (highlighted && data.calls.length) document.getElementById(`call-${highlighted}`)?.scrollIntoView({ block: 'center' }); }, [highlighted, data.calls.length]);

  return <section className="admin-calls" data-testid="admin-calls">
    <div className="admin-calls-head">
      <h2><MessageSquare size={15} /> Recent calls with Jenny</h2>
      <div className="admin-calls-actions">
        <button type="button" className={`admin-filter ${urgentOnly ? 'is-on' : ''}`} aria-pressed={urgentOnly} onClick={() => setUrgentOnly(value => !value)} data-testid="admin-urgent-filter"><AlertTriangle size={12} /> Urgent only{data.urgent_count ? ` (${data.urgent_count})` : ''}</button>
        <button type="button" className="admin-ghost" onClick={load} disabled={loading} data-testid="admin-calls-refresh"><RefreshCw size={12} className={loading ? 'spin' : ''} /> Refresh</button>
      </div>
    </div>
    {data.calls.length ? <ul className="admin-call-list">{data.calls.map(call => <CallRow key={call.call_id} call={call} highlighted={call.call_id === highlighted} />)}</ul>
      : <p className="admin-hint" data-testid="admin-calls-empty">{urgentOnly ? 'No urgent calls flagged.' : 'No calls recorded yet. Records expire 24 hours after the call.'}</p>}
  </section>;
};
